import { request, requestList, rawRequest, buildQuery, orgBase } from '@/api/http'
import type {
  Policy,
  Rule,
  Activation,
  Waiver,
  Deadline,
  HistoryEntry,
  PolicySimResult,
} from '@/api/types'

const base = (orgId: string) => `${orgBase(orgId)}/policies`

export const policiesApi = {
  // ── Policies ───────────────────────────────────────────────────────────────
  list(orgId: string): Promise<Policy[]> {
    return requestList(base(orgId))
  },
  get(orgId: string, pid: string): Promise<Policy> {
    return request(`${base(orgId)}/${pid}`)
  },
  create(orgId: string, body: Partial<Policy> & { rationale: string }): Promise<Policy> {
    return request(base(orgId), { method: 'POST', body: JSON.stringify(body) })
  },
  update(orgId: string, pid: string, body: Partial<Policy> & { rationale: string }): Promise<Policy> {
    return request(`${base(orgId)}/${pid}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  remove(orgId: string, pid: string, rationale: string): Promise<void> {
    return request(`${base(orgId)}/${pid}`, {
      method: 'DELETE',
      body: JSON.stringify({ rationale }),
    })
  },
  clone(orgId: string, pid: string, newName: string, rationale: string): Promise<Policy> {
    return request(`${base(orgId)}/${pid}/clone`, {
      method: 'POST',
      body: JSON.stringify({ new_name: newName, rationale }),
    })
  },
  history(orgId: string, pid: string): Promise<HistoryEntry[]> {
    return requestList(`${base(orgId)}/${pid}/history`)
  },
  async exportYaml(orgId: string, pid: string): Promise<string> {
    const resp = await rawRequest(`${base(orgId)}/${pid}/export`)
    return resp.text()
  },
  import(orgId: string, body: unknown): Promise<Policy> {
    return request(`${base(orgId)}/import`, { method: 'POST', body: JSON.stringify(body) })
  },
  simulate(orgId: string, assets: unknown[]): Promise<PolicySimResult[]> {
    return requestList(`${base(orgId)}/simulate`, { method: 'POST', body: JSON.stringify(assets) })
  },

  // ── Rules (all mutations require a rationale ≥20 chars, §5.3.7) ────────────
  addRule(orgId: string, pid: string, body: Rule, rationale: string): Promise<Rule> {
    return request(`${base(orgId)}/${pid}/rules`, {
      method: 'POST',
      body: JSON.stringify({ ...body, rationale }),
    })
  },
  updateRule(orgId: string, pid: string, ruleId: string, body: Rule, rationale: string): Promise<Rule> {
    return request(`${base(orgId)}/${pid}/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...body, rationale }),
    })
  },
  removeRule(orgId: string, pid: string, ruleId: string, rationale: string): Promise<void> {
    return request(`${base(orgId)}/${pid}/rules/${ruleId}`, {
      method: 'DELETE',
      body: JSON.stringify({ rationale }),
    })
  },

  // ── Activations ──────────────────────────────────────────────────────────
  activations(orgId: string, pid: string): Promise<Activation[]> {
    return requestList(`${base(orgId)}/${pid}/activations`)
  },
  activate(
    orgId: string,
    pid: string,
    body: { scope_type: string; scope_id?: string; rationale: string }
  ): Promise<Activation> {
    return request(`${base(orgId)}/${pid}/activate`, { method: 'POST', body: JSON.stringify(body) })
  },
  deactivate(
    orgId: string,
    pid: string,
    body: { scope_type: string; scope_id?: string; rationale: string }
  ): Promise<void> {
    return request(`${base(orgId)}/${pid}/deactivate`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  // ── Deadlines ──────────────────────────────────────────────────────────────
  deadlines(orgId: string, withinDays = 365): Promise<Deadline[]> {
    return requestList(`${base(orgId)}/deadlines${buildQuery({ within_days: withinDays })}`)
  },
  simulateDeadline(orgId: string, deadline: string): Promise<unknown> {
    return request(`${base(orgId)}/deadlines/simulate${buildQuery({ deadline })}`)
  },

  // ── Waivers ──────────────────────────────────────────────────────────────
  waivers(orgId: string): Promise<Waiver[]> {
    return requestList(`${base(orgId)}/waivers`)
  },
  getWaiver(orgId: string, wid: string): Promise<Waiver> {
    return request(`${base(orgId)}/waivers/${wid}`)
  },
  createWaiver(orgId: string, body: Partial<Waiver>): Promise<Waiver> {
    return request(`${base(orgId)}/waivers`, { method: 'POST', body: JSON.stringify(body) })
  },
  approveWaiver(orgId: string, wid: string, role: string, rationale: string): Promise<Waiver> {
    return request(`${base(orgId)}/waivers/${wid}/approve`, {
      method: 'POST',
      body: JSON.stringify({ role, rationale }),
    })
  },
  revokeWaiver(orgId: string, wid: string, rationale: string): Promise<void> {
    return request(`${base(orgId)}/waivers/${wid}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ rationale }),
    })
  },
  waiverHistory(orgId: string, wid: string): Promise<HistoryEntry[]> {
    return requestList(`${base(orgId)}/waivers/${wid}/history`)
  },
}
